import React, {useMemo, useState} from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel
} from "@mui/material";

const compare = <T, >(a: T, b: T, orderBy: SortDefinition<T>) => {
    const sortBy = orderBy?.sortBy ?? (x => x);
    const valueA = sortBy(access(orderBy, a));
    const valueB = sortBy(access(orderBy, b));
    if (valueA < valueB) {
        return -1;
    }
    if (valueA > valueB) {
        return 1;
    }
    return 0;
};

type Order = 'asc' | 'desc';

const getComparator = <T, >(order: Order, orderBy: SortDefinition<T>) => order === 'asc'
    ? (a: T, b: T) => compare(a, b, orderBy)
    : (a: T, b: T) => -compare(a, b, orderBy);

const access = <T, >(col: RenderDefinition<T>, row: T) => {
    const {accessor} = col;
    return typeof accessor === 'function' ? accessor(row) : row[accessor];
}

const renderCell = <T, >(col: RenderDefinition<T>, row: T) => {
    const {render = x => x} = col;
    return render(access(col, row));
};

export type ColumnDefinition<T> = {
    id: string,
    accessor: keyof T | (<R extends keyof T>(row: T) => T[R]),
    header?: string,
    render?: <R extends keyof T>(value: T[R]) => React.ReactNode,
    sortBy?: <R extends keyof T>(value: T[R]) => string | number,
};

type RenderDefinition<T> = Pick<ColumnDefinition<T>, 'render' | 'accessor'>;
type SortDefinition<T> = Pick<ColumnDefinition<T>, 'id' | 'sortBy' | 'accessor'>;

export const DataTable = <T extends Record<string, any>>(props: {
    data: T[],
    columns: ColumnDefinition<T>[],
}) => {
    const {data, columns} = props;

    const [order, setOrder] = useState<Order>('asc')
    const [orderBy, setOrderBy] = useState<SortDefinition<T> | undefined>(undefined);

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const handleChangePage = (_event: React.MouseEvent | null, page: number) => setPage(page);
    const handleChangePageSize = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setPageSize(Number.parseInt(event.target.value, 10));
        setPage(0);
    };

    const onRequestSort = (col: ColumnDefinition<T>) => {
        setOrder(orderBy?.id === col.id && order === 'asc' ? 'desc' : 'asc');
        setOrderBy(col);
    };

    const dataPage = useMemo(
        () => (orderBy ? [...data].sort(getComparator(order, orderBy)) : data)
            .slice(page * pageSize, page * pageSize + pageSize),
        [data, page, pageSize, order, orderBy]);

    return (
        <Table>
            {columns.some(c => c.header) && (
                <TableHead>
                    {columns.map(col => <TableCell key={col.id} sortDirection={orderBy?.id === col.id ? order : false}>
                        <TableSortLabel active={orderBy?.id === col.id}
                                        direction={orderBy?.id === col.id ? order : 'asc'}
                                        onClick={() => onRequestSort(col)}>
                            {col.header}
                        </TableSortLabel>
                    </TableCell>)}
                </TableHead>
            )}
            <TableBody>
                {dataPage.map((row) => (
                    <TableRow key={row.id}>
                        {columns.map(col => (
                            <TableCell key={col.id}>
                                {renderCell(col, row)}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
            {data.length > pageSize &&
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            colSpan={columns.length}
                            count={data.length}
                            rowsPerPage={pageSize}
                            page={page}
                            onPageChange={handleChangePage}
                            showFirstButton
                            showLastButton
                            onRowsPerPageChange={handleChangePageSize}/>
                    </TableRow>
                </TableFooter>}
        </Table>
    );
};